import { useCallback, useContext } from 'preact/hooks';

import { GlobalsContext } from '@/shared/context';
import { trackMP } from '@/shared/mp';

import { AnimalAlertType } from './constants';
import { AnimalAlertAck, UseAnimalAlertOptions } from './types';
import {
  clearFlaggedAnimalAlertType,
  getFlaggedAnimalAlertType,
  setFlaggedAnimalAlertType,
  useFlaggedAnimalAlertType,
} from './useFlaggedAnimalAlertType';

type EmitAnimalAlertResult = 'accepted' | 'denied' | 'cooldown' | 'unavailable';

/* eslint-disable import/prefer-default-export */
export const useAnimalAlert = ({ habitatId, cameraId, entryPoint }: UseAnimalAlertOptions) => {
  const { socket } = useContext(GlobalsContext);
  const flaggedAlertType = useFlaggedAnimalAlertType(habitatId);

  const emitAnimalAlert = useCallback(
    (alertType: AnimalAlertType): Promise<EmitAnimalAlertResult> => {
      if (!socket || !habitatId || !cameraId) {
        return Promise.resolve('unavailable');
      }

      if (getFlaggedAnimalAlertType(habitatId)) {
        return Promise.resolve('cooldown');
      }

      setFlaggedAnimalAlertType(habitatId, alertType);

      return new Promise((resolve) => {
        socket.emit(
          'animalAlert',
          {
            habitatId,
            cameraId,
            alertType,
          },
          (ack: AnimalAlertAck) => {
            if (ack.ok && ack.status === 'accepted') {
              resolve('accepted');
              return;
            }

            if (!ack.ok && ack.status === 'cooldown') {
              resolve('cooldown');
              return;
            }

            clearFlaggedAnimalAlertType(habitatId);
            resolve('denied');
          },
        );
        // Track the animal alert event no matter it is success or failure
        trackMP('animal-alert', {
          alertType,
          habitatId,
          cameraId,
          entryPoint,
        });
      });
    },
    [cameraId, habitatId, socket, entryPoint],
  );

  return { emitAnimalAlert, flaggedAlertType };
};
